import type { Dispatch } from 'react';
import { useLayoutEffect } from 'react';

import type { FocusOverride, QueryBuilderAction } from '../../../types/QueryBuilder';
import type { ActivePopover } from '../context';
import { focusForTextEditing } from '../utils';

import type { GridSelectionState } from './useGridSelection';

/**
 * Consumes the focusOverride from state after a mutation and applies focus to
 * the target token and sub-cell. Runs synchronously before paint (useLayoutEffect)
 * so focus is applied before the browser renders the update.
 *
 * Token-level virtual focus is updated via GridSelectionState.
 * Sub-cell DOM focus is applied by querying [data-key][data-part].
 */
export function useApplyFocusOverride(
  focusOverride: FocusOverride | null,
  dispatch: Dispatch<QueryBuilderAction>,
  state: GridSelectionState | null,
  wrapperRef: React.RefObject<HTMLElement | null>,
  allKeys: string[],
  setActivePopover?: (popover: ActivePopover) => void,
) {
  useLayoutEffect(() => {
    if (!focusOverride) {
      return;
    }

    const { itemKey, part, caret, activate } = focusOverride;

    const resolvedKey = itemKey === 'end' ? (allKeys.at(-1) ?? null) : itemKey;

    if (state && resolvedKey !== null) {
      state.setFocusedKey(resolvedKey);
    }

    if (wrapperRef.current && resolvedKey !== null) {
      const tokenEl = wrapperRef.current.querySelector<HTMLElement>(`[data-key="${CSS.escape(resolvedKey)}"]`);
      if (part) {
        const cell = tokenEl?.querySelector<HTMLElement>(`[data-part="${CSS.escape(part)}"]`);
        if (cell && (activate || caret)) {
          focusForTextEditing(cell, {
            caret: caret ?? 'end',
            setActivePopover: activate ? setActivePopover : undefined,
          });
        } else {
          cell?.focus();
        }
      } else if (tokenEl) {
        const input =
          tokenEl instanceof HTMLInputElement && !tokenEl.hasAttribute('data-part')
            ? tokenEl
            : tokenEl.querySelector<HTMLInputElement>('input:not([data-part])');
        if (input && caret) {
          focusForTextEditing(input, { caret });
        } else {
          input?.focus();
        }
      }
    }

    dispatch({ type: 'RESET_FOCUS_OVERRIDE' });
  }, [focusOverride, dispatch, state, wrapperRef, allKeys, setActivePopover]);
}
